import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { ClinicProgramRequest, IClinicProgram } from "../library/models/clinicProgram";
import { BaseService } from "./baseService";

/**
 * The days and hours a clinic is open.
 *
 * Neither route names a clinic: the API takes it from the session, so this reads the caller's
 * own program and writes to nobody else's. The read is open to anyone signed in to the clinic,
 * because the therapist's diary draws its hour axis from it; the write is the administrator's
 * alone, and the API refuses either way rather than trusting the tab to have been hidden.
 */
export class ClinicProgramService extends BaseService {
    /**
     * Keyed by the clinic even though the request does not name one -- the same trap
     * `ReminderSettingsService.getSettings` documents: without the tenant in the key, somebody
     * signing out of one clinic and into another in a single page load is served the first
     * clinic's hours from cache. Hand-written rather than built with createGetQuery, whose key
     * is the URL and its query parameters, because the tenant is neither.
     */
    getProgram(tenantId: number | null): UseQueryOptions<IClinicProgram, Error> {
        return {
            queryKey: ['/clinic-program', tenantId],
            queryFn: (): Promise<IClinicProgram> => this.get<IClinicProgram>('/clinic-program'),
        };
    }

    /**
     * Sends all seven days: this is a full replace, not a patch, because "Tuesday was not
     * mentioned" is not a state the backend has an answer for.
     *
     * Hand-written rather than built with createMutation, which only speaks POST/PATCH/DELETE
     * shapes. Resolves with the stored result so the caller renders what was saved rather than
     * trusting its own draft.
     */
    saveProgramMutation(): UseMutationOptions<IClinicProgram, Error, ClinicProgramRequest> {
        return {
            mutationFn: (payload: ClinicProgramRequest): Promise<IClinicProgram> =>
                this.put<IClinicProgram>('/clinic-program', payload),
        };
    }
}
